/*
Toggle class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  Toggle element class button
</toggle-element-class>
*/

export class ToggleElementClass extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      toggleClass: 'menu-drawer--is-active',
      toggleTriggerClass: 'menu-drawer__toggle-button--is-active',
      target: '[data-accessories-drawer]',
      onHover: false,
      hoverDelay: 0,
      preventDefault: true,
      stopPropagation: true,
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);

      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Reduce actions
    this.reducer = {
      click: (e) => this.debounceClickEvent(e),
      mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
      mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
    };
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.target = document.querySelector(this.options.target);

    if (!this.target) {
      return false;
    }

    // Click
    this.addEventListener('click', this.reducer.click, { signal: this.abortController.signal });

    // Mouse enter and leave
    if (this.options.onHover) {
      this.addEventListener('mouseenter', this.reducer.mouseEnter, { signal: this.abortController.signal });
      this.addEventListener('mouseleave', this.reducer.mouseLeave, { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /*
    Debounce click event
    @param e {object}: mouseevent
    Called by add event listener -> reducer.
  */
  debounceClickEvent(e) {
    if (!e) {
      return false;
    }

    this.preventDefault(e);
    this.stopPropagation(e);
    this.toggle();
  }

  /*
    Debounced mouse event
    @param e {object}: mouseevent
    @param type {string}: open/close/closeAll
    Called by add event listener -> reducer.
  */
  debouncedOnMouse(e, type) {
    if (!e) {
      return false;
    }

    // eslint-disable-next-line no-undef, no-shadow
    const debouncer = debounce((type) => {
      switch (type) {
        case 'add':
          this.add();
          break;
        case 'remove':
          this.remove();
          break;
        default:
          this.toggle();
          break;
      }
    }, this.options.hoverDelay);

    debouncer(type);
  }

  // @param e {object}: click event
  preventDefault(e) {
    if (this.options.preventDefault) {
      e.preventDefault();
    }
  }

  // @param e {object}: click event
  stopPropagation(e) {
    if (this.options.stopPropagation) {
      e.stopPropagation();
    }
  }

  add() {
    const { toggleClass, toggleTriggerClass } = this.options;

    if (this.target) {
      this.target.classList.add(toggleClass);
    }

    if (toggleTriggerClass) {
      this.classList.add(toggleTriggerClass);
      this.setAttribute('aria-expanded', 'true');
    }
  }

  remove() {
    const { toggleClass, toggleTriggerClass } = this.options;

    if (this.target) {
      this.target.classList.remove(toggleClass);
    }

    if (toggleTriggerClass) {
      this.classList.remove(toggleTriggerClass);
      this.setAttribute('aria-expanded', 'false');
    }
  }

  toggle() {
    const { toggleClass } = this.options;

    if (!this.target) {
      return false;
    }

    if (!this.target.classList.contains(toggleClass)) {
      this.add();
    } else {
      this.remove();
    }
  }
}
